import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  NgModule,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ObjectType, SchemaService } from '@vendasta/auxiliary-data-components';
import { GalaxyPopoverModule, PopoverPositions } from '@vendasta/galaxy/popover';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { debounceTime, filter, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-external-id',
  templateUrl: './external-id.component.html',
  styleUrls: ['./external-id.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ExternalIdComponent implements OnInit, OnChanges, OnDestroy {
  @Input() externalIdFormControl: UntypedFormControl;
  @Input() createNewField: boolean;
  @Input() name: string;
  @Input() objectType: ObjectType;
  PopoverPositions = PopoverPositions;

  isExternalIdPopoverVisible = false;
  isSmallPopoverVisible = false;
  //TODO instead of using a simple boolean, try to use a Observable
  isExternalIdSet = false;
  editExternalIdText: string;
  isSetButtonDisabled$: Observable<boolean>;
  externalIdNewValue = new UntypedFormControl('', [Validators.pattern('[0-9a-z\\-_]*')]);
  destroy$: Subject<boolean> = new Subject<boolean>();
  isDuplicateExternalId$: Observable<boolean>;
  isCheckingDuplicate = false;

  constructor(
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    private readonly trackingService: ProductAnalyticsService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly schemaService: SchemaService,
  ) {}

  ngOnInit(): void {
    this.editExternalIdText = this.createNewField
      ? 'COMMON.AUXILIARY_DATA.SET_IDENTIFIER'
      : 'COMMON.ACTION_LABELS.EDIT';

    this.externalIdFormControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        filter((_) => !this.isExternalIdSet),
        tap((value) => {
          this.externalIdNewValue.setValue(value);
        }),
      )
      .subscribe();

    const externalIDSearch$ = this.externalIdNewValue.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(400),
      filter((value) => value),
    );

    combineLatest([this.partnerId$, externalIDSearch$])
      .pipe(
        switchMap(([pid, externalIdGet]) => {
          this.isCheckingDuplicate = true;
          return this.schemaService.getMultiFieldSchema(this.objectType, [
            { partnerId: pid, externalId: externalIdGet },
          ]);
        }),
      )
      .subscribe((resp) => {
        this.isCheckingDuplicate = false;
        if (resp.fieldSchemas?.some((fieldSchema) => fieldSchema.fieldId)) {
          this.externalIdNewValue.setErrors({ duplicate: true });
          this.changeDetector.detectChanges();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.name?.currentValue && this.createNewField && !this.isExternalIdSet) {
      const sanitizedId = this.sanitizeName(changes.name.currentValue);
      this.externalIdFormControl.setValue(sanitizedId);
      this.partnerId$
        .pipe(
          debounceTime(300),
          switchMap((pid) => {
            return this.schemaService.getMultiFieldSchema(this.objectType, [
              { partnerId: pid, externalId: sanitizedId },
            ]);
          }),
        )
        .subscribe((resp) => {
          if (resp.fieldSchemas?.some((fieldSchema) => fieldSchema.fieldId)) {
            this.isDuplicateExternalId$ = of(true);
          } else {
            this.isDuplicateExternalId$ = of(false);
          }
        });
    }
  }

  setExternalId(): void {
    this.trackingService.trackEvent(
      'CustomFields:edit-external-id-complete',
      'CustomFields',
      'edit-external-id-complete',
      0,
      {
        objectType: this.objectType,
      },
    );
    this.externalIdFormControl.setValue(this.externalIdNewValue.value);
    this.isExternalIdSet = true;
    this.isExternalIdPopoverVisible = false;
  }

  cancelExternalId(): void {
    this.trackingService.trackEvent(
      'CustomFields:edit-external-id-cancel',
      'CustomFields',
      'edit-external-id-cancel',
      0,
      {
        objectType: this.objectType,
      },
    );
    this.externalIdNewValue.setValue(this.externalIdFormControl.value);
    this.isExternalIdPopoverVisible = false;
    this.isExternalIdSet = false;
  }

  showExternalIdPopover(): void {
    this.trackingService.trackEvent(
      'CustomFields:edit-external-id-start',
      'CustomFields',
      'edit-external-id-start',
      0,
      {
        objectType: this.objectType,
      },
    );
    this.isExternalIdPopoverVisible = true;
  }

  showSmallPopover(): void {
    this.isSmallPopoverVisible = true;
  }

  hideSmallPopover(): void {
    this.isSmallPopoverVisible = false;
  }

  private sanitizeName(name: string): string {
    return name
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^0-9a-z\-_]/g, '');
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}

@NgModule({
  declarations: [ExternalIdComponent],
  exports: [ExternalIdComponent],
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
    TranslateModule,
    GalaxyPopoverModule,
    CommonModule,
  ],
})
export class ExternalIdComponentModule {}
